import { useAppSelector } from "@/app/hooks";
import { resolveDetent } from "@/lib/detents";
import { Divider, Drawer, Stack } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import { navigationMenuLayout } from "../menuSlice";

/** Resting heights as fractions of the viewport; the drawer opens at the first. */
const DETENTS = [0.33, 0.6, 0.9];
/** Movement older than this (ms) at release counts as a hold, not a flick. */
const FLICK_WINDOW = 100;

const detentHeights = () => DETENTS.map((fraction) => fraction * window.innerHeight);

interface SwipeableOffsetDrawerProps {
    children: React.ReactNode;
    title: React.ReactNode;
    opened: boolean;
    onClose: () => void;
}

/** Bottom drawer resized by dragging its header; it settles at the nearest detent when released. */
const SwipeableOffsetDrawer: React.FC<SwipeableOffsetDrawerProps> = ({ children, title, opened, onClose }) => {
    const [drawerHeight, setDrawerHeight] = useState<number>(0);
    const [dragging, setDragging] = useState<boolean>(false);
    const heightRef = useRef(0);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);
    // Last pointer sample, for the release velocity.
    const lastMoveRef = useRef({ y: 0, time: 0, velocity: 0 });
    const currentNavigationMenuLayout = useAppSelector(navigationMenuLayout);

    const resize = (height: number) => {
        heightRef.current = height;
        setDrawerHeight(height);
    };

    useEffect(() => {
        if (opened) resize(detentHeights()[0]);
    }, [opened]);

    useEffect(() => {
        if (!dragging) return;
        const maxHeight = detentHeights().at(-1)!;

        const handleMove = (clientY: number) => {
            const now = performance.now();
            const last = lastMoveRef.current;
            const elapsed = now - last.time;
            // Positive velocity means the finger moved up, growing the drawer.
            if (elapsed > 0) lastMoveRef.current = { y: clientY, time: now, velocity: (last.y - clientY) / elapsed };
            resize(Math.max(0, Math.min(maxHeight, startHeightRef.current - (clientY - startYRef.current))));
        };
        const handleMouseMove = (e: MouseEvent) => handleMove(e.clientY);
        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 1) handleMove(e.touches[0].clientY);
        };
        const endDrag = () => {
            const { time, velocity } = lastMoveRef.current;
            const releaseVelocity = performance.now() - time > FLICK_WINDOW ? 0 : velocity;
            const target = resolveDetent(heightRef.current, releaseVelocity, detentHeights());
            setDragging(false);
            if (target === null) onClose();
            else resize(target);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', endDrag);
        document.addEventListener('touchcancel', endDrag);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', endDrag);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', endDrag);
            document.removeEventListener('touchcancel', endDrag);
        };
    }, [dragging, onClose]);

    const startDrag = (clientY: number) => {
        startYRef.current = clientY;
        startHeightRef.current = heightRef.current;
        lastMoveRef.current = { y: clientY, time: performance.now(), velocity: 0 };
        setDragging(true);
    };
    const handleMouseDown = (e: React.MouseEvent) => {
        startDrag(e.clientY);
        e.preventDefault();
    };
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) startDrag(e.touches[0].clientY);
    };

    return (
        <Drawer
            styles={
                {
                    root: {
                        maxWidth: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
                    },
                    overlay: {
                        marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 65px)',
                        marginLeft: 'env(safe-area-inset-left, 0px)',
                        marginRight: 'env(safe-area-inset-right, 0px)',
                        display: "none"
                    },
                    content: {
                        maxWidth: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
                        marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 65px)',
                        marginLeft: 'env(safe-area-inset-left, 0px)',
                        marginRight: 'env(safe-area-inset-right, 0px)',
                        borderRadius: '10px 10px 0 0',
                        overflow: 'hidden',
                        height: `${drawerHeight}px`,
                        transition: dragging ? 'none' : 'height 0.2s ease',
                    },
                    body: {
                        paddingLeft: 10,
                        paddingRight: 10,
                        paddingBottom: 10,
                        height: '100%'
                    },
                    title: {
                        width: '100%'
                    },
                    header: {
                        paddingTop: 5,
                        paddingBottom: 5
                    }
                }
            }
            opened={opened && currentNavigationMenuLayout === 'default'}
            onClose={onClose}
            padding={10}
            position="bottom"
            withCloseButton={false}
            size={drawerHeight}
            trapFocus={false}
            title={
                <Stack
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart} gap={0}>
                    <Divider
                        my={0}
                        styles={{
                            root: {
                                width: '25dvw',
                                margin: '0 auto'
                            }
                        }} size='lg' />
                    {title}
                    <Divider my={0} size='sm' />
                </Stack>
            }
            transitionProps={{ duration: 0 }}>
            {children}
        </Drawer>
    );
}

export default SwipeableOffsetDrawer;
